/**
 * syncEngine.js — Serial sync: replays queued items one-by-one on reconnect.
 *
 * Only deletes items from the queue after a 200-OK from the server.
 * Returns progress updates via a callback so the UI can render a toast.
 */

import { axiosInstance as axios } from './axios';
import * as queue from './offlineQueue';

/**
 * @typedef {Object} SyncProgress
 * @property {number}  total       — items that were queued when sync started
 * @property {number}  processed   — items successfully synced so far
 * @property {number}  skipped     — items moved to dead-letter (not synced)
 * @property {boolean} done        — true when the batch is finished
 * @property {string|null} error   — last error message (null if ok)
 */

/**
 * Process the offline queue for given user, one item at a time.
 *
 * @param {string}   userId
 * @param {function} onProgress — (SyncProgress) => void
 * @returns {Promise<void>}
 */
export const syncQueue = async (userId, onProgress) => {
  const items = await queue.getAll(userId);
  const total = items.length;
  if (total === 0) return;

  let processed = 0;
  let skipped = 0;
  let hadTransientError = false;
  let lastTransientErrorMsg = null;

  const report = (extra = {}) =>
    onProgress?.({ total, processed, skipped, done: false, error: null, ...extra });

  report();

  for (const item of items) {
    // Transient failures back off exponentially (see offlineQueue.markAttempt)
    // instead of hammering a broken endpoint every single sync cycle. Items
    // not yet due are left in the queue untouched — neither processed nor
    // skipped — and retried on a later cycle.
    if (!queue.isDue(item)) continue;

    try {
      await axios({
        method: item.method || 'post',
        url: item.url,
        data: item.payload,
        headers: item.clientRequestId
          ? { 'X-Offline-Request-Id': item.clientRequestId }
          : undefined,
      });
      // validateStatus accepts only 2xx — non-2xx throws and is handled in catch
      await queue.remove(item.id);
      processed++;
      report({ processed });
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.messages?.[0] || err.message || 'Sync failed';
      const now = Date.now();

      // 401/403 — auth error: stop sync immediately so we don't burn retries on
      // every queued item. The queue is preserved for the next authenticated session.
      if (status === 401 || status === 403) {
        report({ error: `Sync stopped: authentication error (${status})` });
        return;
      }

      // 429 — rate-limited: back off; keep items in the active queue for next sync.
      if (status === 429) {
        report({ error: 'Sync paused: rate limited by server' });
        return;
      }

      // Only dead-letter on payload validation errors (400/422). 5xx and network
      // errors are transient — keep the item in the queue for the next attempt.
      // (Non-validation failures still get their attempt tracked below, just
      // without counting toward the dead-letter threshold.)
      const isValidationError = status === 400 || status === 422;
      if (isValidationError) {
        await queue.markAttempt(item.id, msg);

        if (queue.isPoisoned({ ...item, retryCount: (item.retryCount ?? 0) + 1 })) {
          await queue.moveToDeadLetter(
            { ...item, retryCount: (item.retryCount ?? 0) + 1, lastError: msg, lastAttemptAt: now },
            msg,
          );
          skipped++;
          // Report skipped count only — do NOT set error here because the batch
          // continues; callers treat error as a terminal/paused state.
          report({ skipped });
          continue;
        }
      }

      // Non-validation failures (5xx, network errors, a misbehaving endpoint
      // that redirects into a CORS error, etc.) are transient by design — we
      // never give up on them (no dead-lettering), the item stays queued for
      // a later sync cycle. We still track the attempt (countTowardRetry:
      // false) purely so a permanently broken endpoint backs off instead of
      // re-firing every cycle forever, and so it's visible (lastError) for
      // diagnostics — this must NOT touch retryCount/the dead-letter
      // threshold, otherwise unrelated transient hiccups could push a later
      // payload-validation failure into dead-letter prematurely.
      // A single stuck item must NOT block every other (unrelated, valid)
      // item behind it — report the error for visibility and move on instead
      // of aborting the whole batch.
      if (!isValidationError) {
        await queue.markAttempt(item.id, msg, { countTowardRetry: false });
        hadTransientError = true;
        lastTransientErrorMsg = msg;
      }
      report({ error: msg });
    }
  }

  // The terminal report must NOT blindly claim success. If nothing synced
  // (processed) and nothing was dead-lettered (skipped) — i.e. every
  // attempted item failed transiently with no server response at all —
  // there is zero evidence the server is reachable, so the terminal report
  // must carry that error through instead of resetting to `error: null`
  // (which OfflineProvider reads as "reachable" and would incorrectly turn
  // the dot green and stop the recovery loop). Any real evidence of a
  // server response — a success (processed) or even a validation rejection
  // (skipped, since the server had to respond to reject it) — is enough to
  // report done/ok.
  const hadEvidenceOfReachability = processed > 0 || skipped > 0;
  const terminalError =
    !hadEvidenceOfReachability && hadTransientError ? lastTransientErrorMsg : null;
  onProgress?.({ total, processed, skipped, done: true, error: terminalError });
};
